import Card from '@/components/Card';
import PieArcChart from '@/components/PieArcChart';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { Cpu, Fan, PcCase, Thermometer } from 'lucide-react';
import { BarProgress } from '../DeviceStatusCard/styles';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@mui/material';
import { ChartBox, CoresBox, ThermometerContainer, ValueBox } from './styles';
import { CardTitle } from '../styles';

interface CpuStatsProps {
  metrics?: {
    cpu: {
      modelName: string;
      frequency: string;
      physicalCores: number;
      logicalCores: number;
      systemLoad: string;
      temperature: string;
    };
  };
}

function CpuStats({ metrics }: CpuStatsProps) {
  const { t } = useTranslation();
  const theme = useTheme();

  const cpuUsed = Number(
    metrics?.cpu.systemLoad.replace('%', '').replace(',', '.') || 0
  );

  const cpuPieChartData = [
    {
      value: Number(cpuUsed.toFixed(2)),
      color: 'url(#pieChartGradient)',
      label: t('equipment.used'),
    },
    {
      value: Number((100 - cpuUsed).toFixed(2)),
      color: theme.palette.grey['05'],
      label: t('equipment.free'),
    },
  ];

  return (
    <Card>
      <CardTitle>
        <Fan size="18px" />
        <Typography variant="h4">{t('dashboard.cpu.title')}</Typography>
      </CardTitle>
      <ChartBox>
        <PieArcChart
          data={cpuPieChartData}
          percentage={cpuUsed || 0}
          legendPosition="bottom"
        />
      </ChartBox>
      <Stack direction="column" mt={2} gap={1}>
        {/** THERMOMETER */}
        <ThermometerContainer>
          <Thermometer size="14px" />
          <Typography variant="caption">
            {t('dashboard.cpu.temperature')}:{' '}
          </Typography>
          <Typography
            variant="caption"
            color={theme.palette.white['06']}
            whiteSpace="nowrap"
          >
            {metrics?.cpu.temperature}
          </Typography>
          <BarProgress
            width="100px"
            variant="determinate"
            value={
              (Number(
                (metrics ? metrics.cpu.temperature : '0')
                  .replace('ºC', '')
                  .replace(',', '.')
              ) *
                100) /
              120
            }
            color={theme.palette.primary.main}
          />
        </ThermometerContainer>

        {/** CPU NAME */}
        <ValueBox>
          <PcCase size="14px" />
          <Typography variant="caption">{t('dashboard.cpu.name')} </Typography>
          <Typography variant="caption" color={theme.palette.white['06']}>
            {metrics?.cpu.modelName}
          </Typography>
        </ValueBox>

        {/** CPU NAME */}
        <ValueBox>
          <Fan size="14px" />
          <Typography variant="caption">
            {t('dashboard.cpu.frequency')}{' '}
          </Typography>
          <Typography variant="caption" color={theme.palette.white['06']}>
            {metrics?.cpu.frequency}
          </Typography>
        </ValueBox>

        {/** PHYSICAL CORE */}
        <CoresBox>
          <ValueBox>
            <Cpu size="14px" />
            <Typography variant="caption">
              {t('dashboard.cpu.physicalCores')}
            </Typography>
            <Typography variant="caption" color={theme.palette.white['06']}>
              {metrics?.cpu.physicalCores}
            </Typography>
          </ValueBox>
          <ValueBox>
            <Cpu size="14px" />
            <Typography variant="caption">
              {t('dashboard.cpu.logicalCores')}
            </Typography>
            <Typography variant="caption" color={theme.palette.white['06']}>
              {metrics?.cpu.logicalCores}
            </Typography>
          </ValueBox>
        </CoresBox>
      </Stack>
    </Card>
  );
}

export default CpuStats;
